import { Component, Input } from '@angular/core';
import { DeviceDto } from 'src/app/models/device.model';
import { PartSubType } from 'src/app/models/maintenancelogs.model';
import { UserRole } from 'src/app/models/user.model';
import { ProfileState } from 'src/app/stores/store.states';
import { normalizeDroneConfiguration } from 'src/app/utils/drone-configuration';

type DeviceConfigurationFamily = 'VECTOR' | 'SCORPION';

@Component({
  selector: 'app-device-activity-section',
  templateUrl: './device-activity-section.component.html',
  styleUrl: './device-activity-section.component.scss',
  standalone: false,
})
export class DeviceActivitySectionComponent {
  @Input() device?: DeviceDto;
  @Input() profile: ProfileState | null = null;

  readonly UserRole = UserRole;
  readonly allConfigurationType = PartSubType.All;
  readonly vectorConfigurationType = 'VECTOR';
  readonly scorpionConfigurationType = 'SCORPION';

  configurationType: string = this.allConfigurationType;
  selectedDiagramTabIndex = 0;

  get deviceId(): string | undefined {
    return this.device?.deviceId?.trim() || undefined;
  }

  get hasSessions(): boolean {
    return this.device?.deviceManagementFeatures?.hasSessions === true;
  }

  get hasParts(): boolean {
    return this.device?.deviceManagementFeatures?.hasParts === true;
  }

  get hasPartMaintenance(): boolean {
    return this.device?.deviceManagementFeatures?.hasPartMaintenance === true;
  }

  get hasActivityContent(): boolean {
    return this.canShowDiagrams || this.hasSessions || this.canShowMaintenance;
  }

  get hasMultipleTableSections(): boolean {
    return this.hasSessions && this.canShowMaintenance;
  }

  get canShowDiagrams(): boolean {
    return (
      !!this.deviceId &&
      this.hasParts &&
      this.supportedConfigurationFamilies.length > 0
    );
  }

  get canShowMaintenance(): boolean {
    return this.hasPartMaintenance && !!this.deviceId;
  }

  get supportsVector(): boolean {
    return this.supportedConfigurationFamilies.includes('VECTOR');
  }

  get supportsScorpion(): boolean {
    return this.supportedConfigurationFamilies.includes('SCORPION');
  }

  get supportsMultipleConfigurations(): boolean {
    return this.supportsVector && this.supportsScorpion;
  }

  onDiagramTabChanged(selectedIndex: number | null): void {
    if (!this.supportsMultipleConfigurations) {
      return;
    }

    this.selectedDiagramTabIndex = selectedIndex ?? 0;

    switch (selectedIndex) {
      case 1:
        this.configurationType = this.vectorConfigurationType;
        break;
      case 2:
        this.configurationType = this.scorpionConfigurationType;
        break;
      default:
        this.configurationType = this.allConfigurationType;
        break;
    }
  }

  onVectorConfigurationTypeChanged(type: string): void {
    if (
      this.supportsMultipleConfigurations &&
      this.selectedDiagramTabIndex !== 1
    ) {
      return;
    }

    this.setConfigurationType(type, this.vectorConfigurationType);
  }

  onScorpionConfigurationTypeChanged(type: string): void {
    if (
      this.supportsMultipleConfigurations &&
      this.selectedDiagramTabIndex !== 2
    ) {
      return;
    }

    this.setConfigurationType(type, this.scorpionConfigurationType);
  }

  private get supportedConfigurationFamilies(): DeviceConfigurationFamily[] {
    const configuredFamilies =
      this.device?.deviceManagementFeatures?.multiConfigurationSupport ?? [];

    const families = configuredFamilies
      .map((configuration) => this.resolveConfigurationFamily(configuration))
      .filter(
        (configuration): configuration is DeviceConfigurationFamily =>
          !!configuration,
      );

    return Array.from(new Set(families));
  }

  private resolveConfigurationFamily(
    configuration: string,
  ): DeviceConfigurationFamily | undefined {
    const normalizedConfiguration = normalizeDroneConfiguration(configuration);

    if (normalizedConfiguration.includes('vector')) {
      return 'VECTOR';
    }

    if (normalizedConfiguration.includes('scorpion')) {
      return 'SCORPION';
    }

    return undefined;
  }

  private setConfigurationType(type: string, fallbackType: string): void {
    this.configurationType = type || fallbackType;
  }
}
